import { useMemo } from 'react'
import { useQueries } from 'react-query'
import dataAPI, { getRelayStatus } from '../../Jouly/services/dataAPI'
import { useFilter } from '../../filter/hook'

export type TriStateFlag = 'ON' | 'OFF' | 'NA'

export interface MeterTelemetrySnapshot {
  onlineFlag: TriStateFlag
  relayFlag: TriStateFlag
  consumptionIndex: number | null
  credit: number | null
}

const ONLINE_WINDOW_MS = 2 * 60 * 60 * 1000

const computeSnapshot = (
  heartbeatTime: string | undefined,
  relayOn: boolean | undefined,
  consumptionValue: number | undefined,
  creditValue: number | undefined,
): MeterTelemetrySnapshot => {
  // Binaire : un compteur sans heartbeat récent est considéré hors ligne.
  // Garantit Online + Offline = Total (pas de bucket NA caché).
  let onlineFlag: TriStateFlag = 'OFF'
  if (heartbeatTime) {
    const t = new Date(heartbeatTime).getTime()
    if (!Number.isNaN(t) && Date.now() - t < ONLINE_WINDOW_MS) {
      onlineFlag = 'ON'
    }
  }

  const relayFlag: TriStateFlag = relayOn === true ? 'ON' : relayOn === false ? 'OFF' : 'NA'

  return {
    onlineFlag,
    relayFlag,
    consumptionIndex: consumptionValue ?? null,
    credit: creditValue ?? null,
  }
}

export const useMetersTelemetrySnapshot = (productNumbers: string[]) => {
  const { filter } = useFilter()
  const filterDeviceName = filter.device_name

  const heartbeatQueries = useQueries(
    productNumbers.map((pn) => ({
      queryKey: ['heartbeat-meter', filterDeviceName, pn],
      queryFn: () => dataAPI.getLastHeartbeatSingleCounter(filter, pn),
      enabled: Boolean(pn),
    })),
  )

  const relayQueries = useQueries(
    productNumbers.map((pn) => ({
      queryKey: ['relay-status', pn],
      queryFn: () => getRelayStatus(pn),
      enabled: Boolean(pn),
    })),
  )

  const consumptionQueries = useQueries(
    productNumbers.map((pn) => ({
      queryKey: ['consumption-index-last', pn],
      queryFn: () => dataAPI.getLastConsumptionIndexSingleCounter({ device_name: pn }),
      enabled: Boolean(pn),
    })),
  )

  const creditQueries = useQueries(
    productNumbers.map((pn) => ({
      queryKey: ['credit-last', pn],
      queryFn: () => dataAPI.getLastCreditSingleCounter({ device_name: pn }),
      enabled: Boolean(pn),
    })),
  )

  const productNumbersKey = productNumbers.join(',')
  const heartbeatUpdatedKey = heartbeatQueries.map((q) => q.dataUpdatedAt).join(',')
  const relayUpdatedKey = relayQueries.map((q) => q.dataUpdatedAt).join(',')
  const consumptionUpdatedKey = consumptionQueries.map((q) => q.dataUpdatedAt).join(',')
  const creditUpdatedKey = creditQueries.map((q) => q.dataUpdatedAt).join(',')

  const byProductNumber = useMemo(() => {
    const map: Record<string, MeterTelemetrySnapshot> = {}
    productNumbers.forEach((pn, i) => {
      if (!pn) return
      const hb = heartbeatQueries[i]?.data as Array<{ Time?: string }> | undefined
      const rl = relayQueries[i]?.data
      const cs = consumptionQueries[i]?.data as Array<{ Value?: number }> | undefined
      const cr = creditQueries[i]?.data as Array<{ Value?: number }> | undefined
      map[pn] = computeSnapshot(hb?.[0]?.Time, rl?.relay_on, cs?.[0]?.Value, cr?.[0]?.Value)
    })
    return map
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    productNumbersKey,
    heartbeatUpdatedKey,
    relayUpdatedKey,
    consumptionUpdatedKey,
    creditUpdatedKey,
  ])

  const isLoading =
    heartbeatQueries.some((q) => q.isLoading) ||
    relayQueries.some((q) => q.isLoading) ||
    consumptionQueries.some((q) => q.isLoading) ||
    creditQueries.some((q) => q.isLoading)

  return { byProductNumber, isLoading }
}

export default useMetersTelemetrySnapshot
